import React, { useState, useRef, useEffect } from 'react';
import '../../styles/components/audio-player-slider.css';
import { PlayButton, PauseButton } from '../../assets/images';

type Audioprops = {
  audioUrl: string;
  audioLength: number;
};

const AudioPlayer: React.FunctionComponent<Audioprops> = ({ audioUrl, audioLength }) => {
  // state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0); // This is used to actually stop the audio.
  const displayDuration = Math.floor(duration); // This is used to show nice, even seconds.

  // references
  const audioPlayer: any = useRef(); // reference our audio component
  const progressBar: any = useRef(); // reference our progress bar
  const animationRef: any = useRef(); // reference the animation

  useEffect(() => {
    const handleLoadedMetadata = (e: any) => {
      const seconds = e.target.duration;
      setDuration(seconds);
      if (progressBar?.current) {
        progressBar.current.max = seconds;
      }
    };

    if (audioPlayer?.current && progressBar?.current) {
      audioPlayer.current.addEventListener('loadedmetadata', handleLoadedMetadata);
    }

    return () => {
      if (audioPlayer?.current) {
        audioPlayer.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    if (audioUrl && audioPlayer?.current) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audioPlayer.current.pause();
      setCurrentTime(0);
      setIsPlaying(false);
      audioPlayer.current.currentTime = 0;
      if (progressBar?.current) {
        progressBar.current.value = 0;
      }
      audioPlayer.current.load();
    }
  }, [audioUrl]);

  useEffect(() => {
    if (progressBar?.current && audioLength) {
      progressBar.current.max = audioLength;
    }
  }, [audioLength]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioPlayer?.current) {
        audioPlayer.current.pause();
      }
    };
  }, []);

  const calculateTime = (secs: any) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  const togglePlayPause = async () => {
    if (!audioPlayer?.current) return;

    const prevValue = isPlaying;
    setIsPlaying(!prevValue);
    if (!prevValue) {
      try {
        audioPlayer.current.volume = 1.0;
        audioPlayer.current.muted = false;
        await audioPlayer.current.play();
        animationRef.current = requestAnimationFrame(whilePlaying);
      } catch (error) {
        console.error('Play error:', error);
        setIsPlaying(false);
      }
    } else {
      audioPlayer.current.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  const resetDefault = () => {
    if (audioPlayer?.current && progressBar?.current) {
      audioPlayer.current.pause();
      setCurrentTime(0);
      setIsPlaying(false);
      audioPlayer.current.currentTime = 0;
      progressBar.current.value = 0;
    }
  };

  const whilePlaying = () => {
    if (!audioPlayer?.current || !progressBar?.current) {
      return;
    }
    const { currentTime } = audioPlayer.current;
    if (+currentTime >= duration) {
      resetDefault();
    } else {
      progressBar.current.value = currentTime;
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
    changePlayerCurrentTime();
  };

  const changeRange = () => {
    if (audioPlayer?.current && progressBar?.current) {
      audioPlayer.current.currentTime = progressBar.current.value;
      changePlayerCurrentTime();
    }
  };

  const changePlayerCurrentTime = () => {
    if (progressBar?.current) {
      progressBar.current.style.setProperty('--seek-before-width', `${(progressBar.current.value / duration) * 100}%`);
      setCurrentTime(progressBar.current.value);
    }
  };

  return (
    <div className="audioPlayer w-full">
      <audio ref={audioPlayer} src={audioUrl} preload="metadata" crossOrigin="anonymous"></audio>

      <img
        className={isPlaying ? 'w-25' : 'w-24'}
        src={isPlaying ? PauseButton : PlayButton}
        role="button"
        alt="play-button"
        onClick={togglePlayPause}
      />

      <div className="mt-7 w-full pl-12">
        {/* progress bar */}

        <input type="range" className="progressBar" defaultValue="0" ref={progressBar} onChange={changeRange} />
        {/* current time */}
        <div className="flex justify-between pt-4">
          <p>{calculateTime(currentTime)}</p>
          <p>{displayDuration && !isNaN(displayDuration) && calculateTime(displayDuration)}</p>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
